import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useRouter } from 'next/router'
import * as React from 'react'
import { useIntl } from 'react-intl'
import { useCMSPageField } from '@/cms/fields'
import {
  useBookingNumber,
  useGetGroupStatus,
  useTransformBookingToCheckoutQuery,
} from '@/domain/groups/hooks'
import { groupBookingsAPIClient } from '@/infrastructure/groups/api'
import { Custom500 } from '@/pages/500'
import { BasketLegacy } from '@/scopes/checkout/components/Basket/BasketLegacy'
import { CheckoutError } from '@/scopes/checkout/components/CheckoutError/CheckoutError'
import { CheckoutHeader } from '@/scopes/checkout/components/CheckoutHeader'
import {
  CheckoutHeading,
  CheckoutHeadingDescription,
  CheckoutHeadingTitle,
} from '@/scopes/checkout/components/CheckoutHeading'
import { CheckoutNextStep } from '@/scopes/checkout/components/CheckoutNextStep'
import { CheckoutProgress } from '@/scopes/checkout/components/CheckoutProgress'
import { Content, Loader, MainContent, PrimaryContent, SideBarContent } from '@/scopes/checkout/components/Layout'
import { Services } from '@/scopes/checkout/components/services'
import { useSendCheckoutEvent } from '@/scopes/checkout/domain/analytics/useSendCheckoutEvent'
import { useSendImpressionViewAncillaries } from '@/scopes/checkout/domain/analytics/useSendImpressionViewAncillaries'

import { CheckoutReactContext } from '@/scopes/checkout/domain/CheckoutContext'
import { useBookingDraftQuery, useCheckoutSid } from '@/scopes/checkout/domain/hooks'
import {
  PageLayout,
} from '@/scopes/checkout/groups/components/PageLayout'
import { RoomSelectionService } from '@/scopes/checkout/groups/components/services/RoomSelection/RoomSelectionService'
import StatusAlert from '@/scopes/checkout/groups/components/StatusAlert'
import { StatusNotification } from '@/scopes/checkout/groups/components/StatusNotification'
import { GroupsDependenciesProvider } from '@/scopes/checkout/groups/dependencies/context'
import { QueueDependenciesProvider, useQueueActions } from '@/scopes/checkout/groups/dependencies/queueContext'
import { queueActions } from '@/scopes/checkout/groups/domain/actions/queue'
import { createQueueAPIClient } from '@/scopes/checkout/groups/infrastructure/api/queueAPIClient'
import { queueStore } from '@/scopes/checkout/groups/infrastructure/store/queueStore'
import { useQueueStore } from '@/scopes/checkout/groups/infrastructure/store/useQueueStore'
import { checkoutAPIClient } from '@/scopes/checkout/infrastructure/api'
import { Hero } from '@/scopes/checkout/pages/groups/Components/Hero'
import Queue from '@/scopes/checkout/pages/groups/Components/Queue/Queue'
import { isFetchedSelector, waitingTimeSelector } from '@/scopes/checkout/pages/groups/selectors'
import styles from '@/scopes/checkout/pages/Pages.module.css'
import { DevTools } from '@/scopes/common/components/DevTools/DevTools'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import { NewRelicErrorBoundary } from '@/scopes/common/infrastructure/logging/NewRelicErrorBoundary'
import { createNewRelicLogger } from '@/scopes/common/infrastructure/logging/newRelicLogger'
import createNextRouter from '@/scopes/common/infrastructure/router/nextRouter'

const queryClient = new QueryClient()

function PageHeading() {
  const intl = useIntl()
  const { data } = useBookingDraftQuery()

  return (
    <CheckoutHeading>
      <CheckoutHeadingTitle>
        {intl.formatMessage(
          { id: 'groups_checkout_steps_extra_services_title' },
          { group_name: data?.groupInformation?.name ?? '' },
        )}
      </CheckoutHeadingTitle>
      <CheckoutHeadingDescription>
        {intl.formatMessage({ id: 'groups_checkout_steps_extra_services_description' })}
      </CheckoutHeadingDescription>
    </CheckoutHeading>
  )
}

function Page() {
  const sid = useCheckoutSid()
  const { data } = useBookingDraftQuery()
  const groupsHeader = useCMSPageField('Groups.header') ?? null

  if (!sid) {
    return null
  }

  return (
    <>
      {data?.error?.hasError && <CheckoutError showRedirectButton />}
      {(!data?.error || (data?.error && !data.error.hasError)) && (
        <>
          {groupsHeader && <Hero />}
          <MainContent>
            {!groupsHeader && <CheckoutProgress />}
            <Content>
              <PrimaryContent>
                {!groupsHeader && <PageHeading />}
                <div className={styles.wrapper}>
                  {data?.groupInformation?.roomSelectionIsEnabled && <RoomSelectionService />}
                  <Services />
                </div>
                <CheckoutNextStep sendSubmitEvent />
              </PrimaryContent>
              <SideBarContent>
                {/* Group checkout always uses BasketLegacy: the new Basket doesn't yet render groupInformation (name/size). */}
                <BasketLegacy />
              </SideBarContent>
            </Content>
          </MainContent>
        </>
      )}
    </>
  )
}

function WrapperContent() {
  const bookingNumber = useBookingNumber()
  const sid = useCheckoutSid()
  const { isLoading: getStatusIsLoading, isFetched: getStatusIsFetched, data: getStatusData } = useGetGroupStatus()

  const { isLoading: transformIsLoading, isFetched: transformIsFetched, data: transformBookingData, isError: transformIsError } = useTransformBookingToCheckoutQuery()

  const { isLoading, isFetching } = useBookingDraftQuery()

  const [isPageLoading, setIsPageLoading] = React.useState(true)

  useSendCheckoutEvent()
  useSendImpressionViewAncillaries()

  React.useEffect(() => {
    if (sid) {
      setIsPageLoading(false)
    }
    if (getStatusIsFetched && !getStatusData?.isValid) {
      setIsPageLoading(false)
    }
    if (
      transformIsFetched
      && getStatusIsFetched
      && !isFetching
      && !(isLoading && getStatusIsLoading && transformIsLoading)
    ) {
      setIsPageLoading(false)
    }
  }, [
    getStatusData,
    sid,
    transformIsFetched,
    getStatusIsFetched,
    isFetching,
    isLoading,
    transformIsLoading,
    getStatusIsLoading,
  ])

  return (
    <>
      {isPageLoading
        ? (
            <Loader />
          )
        : (
            <>
              {bookingNumber && !sid && <StatusAlert />}
              {(transformIsError || (transformBookingData && transformBookingData.error?.hasError)) && (
                <StatusNotification status="error" groupName={getStatusData?.groupName} />
              )}
              <Page />
            </>
          )}
    </>
  )
}

export default function CheckoutPageStep1() {
  const router = useRouter()
  const contextItemId = useCMSPageField('contextItemId')
  const [sid, setSid] = React.useState<string | null>('')
  const checkoutContextValue = React.useMemo(() => ({
    contextItemId,
    draftId: sid || null,
    setDraftId: setSid,
    step: 1,
  }), [contextItemId, sid])

  return (
    <QueryClientProvider client={queryClient}>
      <CheckoutReactContext value={checkoutContextValue}>
        <GroupsDependenciesProvider
          dependencies={{ checkoutAPIClient, groupBookingsAPIClient, router: createNextRouter(router) }}
        >
          <QueueProvider>
            <QueueExtrasLayout />
            <DevTools />
          </QueueProvider>
        </GroupsDependenciesProvider>
      </CheckoutReactContext>
    </QueryClientProvider>
  )
}

function QueueExtrasLayout() {
  const actions = useQueueActions()
  const isFetched = useQueueStore(isFetchedSelector)
  const waitingTime = useQueueStore(waitingTimeSelector)
  const [canEnter, setCanEnter] = React.useState(false)

  React.useEffect(() => {
    const load = async () => {
      await actions.pageLoaded()
    }
    load()
  }, [actions])

  if ((isFetched && waitingTime === 0) || canEnter) {
    return <ExtrasLayout />
  }

  return (
    <PageLayout>
      <CheckoutHeader showLoginMenu={false} showBackButton={false} />
      <MainContent>
        {!isFetched ? <Loader /> : <Queue waitingTime={waitingTime} onCountdownEnd={() => setCanEnter(true)} />}
      </MainContent>
    </PageLayout>
  )
}

function ExtrasLayout() {
  const brandName = useConfiguration('brandName')

  return (
    <PageLayout>
      <NewRelicErrorBoundary scope="groups-page" fallback={<Custom500 brand={brandName} />}>
        <CheckoutHeader showLoginMenu={false} showBackButton={false} />
        <WrapperContent />
      </NewRelicErrorBoundary>
    </PageLayout>
  )
}

function QueueProvider({ children }: React.PropsWithChildren) {
  const queueApiClient = React.useMemo(() => createQueueAPIClient(), [])
  const newRelicLogger = React.useMemo(() => createNewRelicLogger({ scope: 'checkout' }), [])

  const actions = React.useMemo(
    () =>
      queueActions({
        store: queueStore,
        client: queueApiClient,
        logger: newRelicLogger,
      }),
    [queueApiClient, newRelicLogger],
  )

  return (
    <QueueDependenciesProvider dependencies={{ actions }}>
      {children}
    </QueueDependenciesProvider>
  )
}
